import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { AdventureSessionSummaryDto } from '../../types/adventure';
import { getAdventureTaskImageSrc, getCompletedAdventureStepCount } from '../../utils/adventurePresentation';
import { formatChinaMonthDayTime } from '../../utils/chinaTime';
import rainforestSceneImage from '../../assets/adventure-scenes/rainforest.jpeg';
import desertSceneImage from '../../assets/adventure-scenes/desert.jpg';
import glacierSceneImage from '../../assets/adventure-scenes/glacier.jpg';
import caveSceneImage from '../../assets/adventure-scenes/cave.jpeg';

const STEP_LABELS: Record<string, string> = {
  AVATAR: '选择形象',
  TEAM: '选择队友',
  SCENE: '选择场景',
  EQUIPMENT: '选择装备',
  DANGER: '设计险情',
  ESCAPE: '展开逃生',
};

const formatTime = (value?: string) => {
  if (!value) {
    return '刚刚';
  }

  return formatChinaMonthDayTime(value) || '刚刚';
};

const ADVENTURE_ENTRY_TITLE = '神奇的探险之旅';

export const AdventureEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [sessions, setSessions] = useState<AdventureSessionSummaryDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const latestSession = sessions[0];
  const entryImageSrc = getAdventureTaskImageSrc(ADVENTURE_ENTRY_TITLE);

  useEffect(() => {
    let active = true;

    const loadSessions = async () => {
      if (!assignmentId) {
        setLoadError('缺少任务信息，请返回首页重新进入。');
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get<AdventureSessionSummaryDto[]>(`/assignments/${assignmentId}/adventure-sessions`);
        if (!active) {
          return;
        }

        setSessions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (active) {
          setLoadError('读取探险记录失败，请稍后重试。');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void loadSessions();

    return () => {
      active = false;
    };
  }, [assignmentId]);

  const historyCards = useMemo(() => sessions.slice(1), [sessions]);

  const handleCreateNew = async () => {
    if (!assignmentId) {
      return;
    }

    try {
      const response = await api.post<{ id: string }>(`/assignments/${assignmentId}/adventure-sessions`);
      const sessionId = response.data?.id;
      navigate(sessionId ? `/student/adventure/${assignmentId}?sessionId=${sessionId}` : `/student/adventure/${assignmentId}`);
    } catch (error) {
      setLoadError('新建探险失败，请稍后重试。');
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#eef7ef] text-slate-900">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,28,21,0.2),rgba(8,28,21,0.45))]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,245,200,0.3),transparent_34%),radial-gradient(circle_at_top_right,rgba(173,230,220,0.24),transparent_36%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(0,0,0,0.25))]" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-80"
          style={{ backgroundImage: `url(${rainforestSceneImage})` }}
        />
        <div
          className="absolute bottom-0 left-0 h-[42vh] w-[36vw] bg-cover bg-center opacity-70 mix-blend-screen"
          style={{ backgroundImage: `url(${desertSceneImage})`, clipPath: 'polygon(0 18%,100% 0,100% 100%,0 100%)' }}
        />
        <div
          className="absolute right-0 top-0 h-[45vh] w-[34vw] bg-cover bg-center opacity-55 mix-blend-screen"
          style={{ backgroundImage: `url(${glacierSceneImage})`, clipPath: 'polygon(10% 0,100% 0,100% 100%,0 82%)' }}
        />
        <div
          className="absolute right-[8%] bottom-[10%] h-[28vh] w-[22vw] min-w-[220px] bg-cover bg-center opacity-55"
          style={{ backgroundImage: `url(${caveSceneImage})`, filter: 'saturate(0.92)' }}
        />
      </div>

      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute left-[7%] top-[12%] text-5xl text-amber-100/70">🧭</div>
        <div className="absolute right-[11%] top-[18%] text-4xl text-emerald-100/70">🍃</div>
        <div className="absolute left-[12%] bottom-[15%] text-4xl text-slate-100/60">👣</div>
        <div className="absolute right-[18%] bottom-[12%] text-4xl text-orange-100/70">🔎</div>
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-8 md:px-8">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/student')}
            className="rounded-full border border-white/35 bg-white/12 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md transition hover:bg-white/18"
          >
            ← 返回任务卡
          </button>
          <p className="text-sm font-semibold tracking-[0.28em] text-white/85">{ADVENTURE_ENTRY_TITLE}</p>
        </div>

        <section className="mt-10 flex-1 rounded-[40px] border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.16),rgba(255,255,255,0.08))] p-5 text-white shadow-[0_28px_90px_rgba(0,0,0,0.24)] backdrop-blur-xl md:p-8">
          <div className="rounded-[32px] border border-white/20 bg-[linear-gradient(180deg,rgba(18,52,40,0.34),rgba(18,52,40,0.18))] p-6 md:p-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-100/80">探险入口</p>
                <h1 className="mt-3 text-4xl font-black tracking-tight text-white md:text-6xl">{ADVENTURE_ENTRY_TITLE}</h1>
                <p className="mt-4 text-base leading-8 text-white/88 md:text-lg">
                  从这里开始你的探险故事。先继续上次的记录，或者重新开启一段新的旅程。
                </p>
              </div>
              {entryImageSrc ? (
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(true)}
                  aria-label={`预览${ADVENTURE_ENTRY_TITLE}入口图片`}
                  className="group relative w-fit overflow-hidden rounded-[28px] border border-white/30 bg-white/10 shadow-[0_18px_48px_rgba(0,0,0,0.22)] backdrop-blur-md transition hover:-translate-y-0.5 hover:bg-white/14"
                >
                  <img
                    src={entryImageSrc}
                    alt={`${ADVENTURE_ENTRY_TITLE}入口缩略图`}
                    loading="lazy"
                    className="h-28 w-28 object-cover md:h-36 md:w-36"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/75 via-slate-950/20 to-transparent px-3 py-2 text-left text-xs font-semibold text-white">
                    点击查看大图
                  </div>
                </button>
              ) : null}
            </div>
          </div>

          <div className="mt-6 rounded-[32px] border border-white/30 bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(245,251,247,0.9))] p-6 text-slate-900 shadow-[0_18px_60px_rgba(0,0,0,0.16)] md:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400">探险记录</p>
                <h2 className="mt-2 text-3xl font-black text-slate-900">选择你的出发方式</h2>
              </div>
              <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-right">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-500">旅程状态</p>
                <p className="mt-1 text-lg font-black text-emerald-700">{latestSession ? `已完成 ${getCompletedAdventureStepCount(latestSession)}/5` : '准备启程'}</p>
              </div>
            </div>

            {isLoading ? (
              <div className="mt-8 rounded-3xl bg-slate-50 p-6 text-center text-slate-500">正在读取探险记录...</div>
            ) : loadError ? (
              <div className="mt-8 rounded-3xl border border-rose-100 bg-rose-50 p-6 text-center text-rose-600">{loadError}</div>
            ) : (
              <div className="mt-8 space-y-5">
                {latestSession ? (
                  <div className="rounded-[28px] border border-emerald-100 bg-[linear-gradient(135deg,#ecfdf5,white_58%)] p-5 shadow-sm">
                    <p className="text-sm font-semibold text-emerald-700">继续上次探险</p>
                    <h3 className="mt-2 text-xl font-black text-slate-900">
                      上次进行到「{STEP_LABELS[latestSession.currentStep] ?? latestSession.currentStep}」
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">
                      已完成 {getCompletedAdventureStepCount(latestSession)}/5 步 · 最近保存于 {formatTime(latestSession.lastActiveAt)}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/student/adventure/${assignmentId}?sessionId=${latestSession.id}`)}
                        className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700"
                      >
                        继续
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateNew}
                        className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50"
                      >
                        新开探险
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-[28px] border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
                    <p className="text-sm text-slate-500">还没有历史记录，可以直接开启第一段探险。</p>
                    <button
                      type="button"
                      onClick={handleCreateNew}
                      className="mt-4 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700"
                    >
                      立即出发
                    </button>
                  </div>
                )}

                {historyCards.length > 0 && (
                  <div className="rounded-[28px] border border-slate-200 bg-white/70 p-5">
                    <p className="text-sm font-semibold text-slate-700">历史记录</p>
                    <div className="mt-4 space-y-3">
                      {historyCards.map((session, index) => (
                        <div
                          key={session.id}
                          className="flex flex-col gap-3 rounded-2xl bg-slate-50/90 p-4 md:flex-row md:items-center md:justify-between"
                        >
                          <div>
                            <p className="text-sm font-semibold text-slate-900">探险记录 #{historyCards.length - index}</p>
                            <p className="mt-1 text-sm text-slate-600">
                              进行到「{STEP_LABELS[session.currentStep] ?? session.currentStep}」 · 已完成 {getCompletedAdventureStepCount(session)}/5 步
                            </p>
                            <p className="mt-1 text-xs text-slate-400">最近活跃：{formatTime(session.lastActiveAt)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => navigate(`/student/adventure/${assignmentId}?sessionId=${session.id}`)}
                            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50"
                          >
                            继续
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {isPreviewOpen && entryImageSrc ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6" role="dialog" aria-label="入口图片预览">
          <button
            type="button"
            aria-label="关闭入口图片预览"
            className="absolute inset-0 cursor-default"
            onClick={() => setIsPreviewOpen(false)}
          />
          <div className="relative z-10 w-full max-w-4xl rounded-[2rem] bg-white p-4 shadow-2xl">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-emerald-600">入口图片预览</p>
                <h3 className="text-xl font-black text-slate-900">{ADVENTURE_ENTRY_TITLE}</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsPreviewOpen(false)}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
              >
                关闭
              </button>
            </div>
            <img src={entryImageSrc} alt={`${ADVENTURE_ENTRY_TITLE}入口大图`} className="mx-auto max-h-[80vh] w-full rounded-[1.5rem] object-contain" />
          </div>
        </div>
      ) : null}
    </div>
  );
};
